import { useQuery } from '@tanstack/react-query'
import { RefreshCw } from 'lucide-react'
import { api } from '@/lib/api'
import type { SourceReliabilityReport, SourceReliabilitySource } from '@/lib/api'

// Control-room health triad
export const OK = 'hsl(150 50% 55%)'
export const WARN = 'hsl(35 90% 60%)'
export const ERR = 'hsl(3 100% 67%)'
export const DIM = 'hsl(216 13% 42%)'
export const healthColor = (score: number) => (score >= 90 ? OK : score >= 70 ? WARN : ERR)

export const reliabilityStateColor = (state?: string) => {
  switch (state) {
    case 'healthy':
      return OK
    case 'warning':
    case 'degraded':
      return WARN
    case 'failed':
      return ERR
    default:
      return DIM
  }
}

export function SourceStatePill({ source }: { source: SourceReliabilitySource }) {
  const color = reliabilityStateColor(source.health_state)
  return (
    <span className="cr-mono text-[9px] tracking-[0.06em] uppercase" style={{ color }}>
      {(source.health_state || 'unknown').replace('_', ' ')} {Math.round(source.health_score)}
    </span>
  )
}

// Per-source reliability breakdown for a stream: which source is IN USE,
// per-source health state, domain, failure count. Data from
// GET /streams/{id}/reliability.
export function StreamReliabilityPanel({ streamId }: { streamId: string }) {
  const reliabilityQuery = useQuery<SourceReliabilityReport>({
    queryKey: ['stream-reliability', streamId],
    queryFn: () => api.getStreamReliability(streamId),
    refetchInterval: 10000,
  })

  if (reliabilityQuery.isLoading) {
    return <div className="text-xs text-muted-foreground py-2">Loading reliability…</div>
  }
  if (!reliabilityQuery.data) {
    return <div className="text-xs text-muted-foreground py-2">Reliability details unavailable.</div>
  }

  const report = reliabilityQuery.data
  const currentSource = report.sources.find((s) => s.is_current)

  return (
    <div className="flex flex-col gap-1.5 py-2">
      <div className="flex items-center justify-between gap-2">
        <span className="cr-label">
          SOURCES · {currentSource ? `#${currentSource.index + 1} OF ${report.sources.length} IN USE` : 'NONE ACTIVE'}
          {report.state ? ` · ${String(report.state).toUpperCase()}` : ''}
        </span>
        <button
          type="button"
          onClick={() => reliabilityQuery.refetch()}
          className="p-1 rounded text-muted-foreground hover:text-foreground transition-colors"
          title="Refresh reliability"
        >
          <RefreshCw className={`w-3 h-3 ${reliabilityQuery.isFetching ? 'animate-spin' : ''}`} />
        </button>
      </div>
      {report.sources.map((source) => (
        <div
          key={source.index}
          className="grid items-center gap-2.5 py-1 cr-row last:border-b-0"
          style={{ gridTemplateColumns: '26px 92px 1fr auto' }}
        >
          <span className="cr-mono text-[10px] text-muted-foreground">P{source.index + 1}</span>
          <SourceStatePill source={source} />
          <span
            className="cr-mono text-[10px] text-muted-foreground truncate"
            title={source.health_reason || source.probe_warning || source.last_failure_reason || undefined}
          >
            {source.domain || 'unknown'}
            {source.connection_group ? ` · ${source.connection_group}` : ''}
            {source.failure_count > 0 ? ` · ${source.failure_count} fail` : ''}
          </span>
          {source.is_current ? (
            <span className="cr-mono text-[9px]" style={{ color: OK }}>
              ● IN USE
            </span>
          ) : (
            <span />
          )}
        </div>
      ))}
      {(currentSource?.health_reason || currentSource?.probe_warning || currentSource?.last_failure_reason) && (
        <div className="text-[11px]" style={{ color: WARN }}>
          {currentSource.health_reason || currentSource.probe_warning || currentSource.last_failure_reason}
        </div>
      )}
    </div>
  )
}
